import ColorPickerModal from "../modals/ColorPickerModal";
import MillingSelector from "../helpers/MillingSelector";
// src/modules/components/createdItemsPage.js
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  memo
} from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useNavigationGuard } from "../helpers/NavigationGuardContext";
import { getAllColors } from "../../data/getters";
import createdItemApi from "../../data/api/createdItemApi";
import { IonIcon } from "@ionic/react";
import { reload, trashOutline, arrowUndoOutline, arrowRedoOutline } from "ionicons/icons";
import { useLanguage } from "../helpers/LanguageContext";
import {
  buildKantString,
  buildItemUpdatePayload,
  buildSizeString,
  cantTypeOptions,
  diffChangedItems,
  normalizeCantTypeInput,
  parseKant,
  parseSize
} from "./createdItemsPageUtil";
import "../../css/CreatedItemsPage.css";

/* ----------------- Segédfüggvények ----------------- */
const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000
};
const modalStyle = {
  background: "#fff",
  padding: 16,
  borderRadius: 6,
  width: "min(900px, 92vw)",
  maxHeight: "80dvh",
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(0,0,0,.2)"
};
const FIX_THICKNESS = 18;

const FIELD_LIMITS = {
  name: 200,
  length: 10000,
  width: 10000,
  qty: 1000,
  details: 500
};

const clampNumber = (value, max) =>
  Math.min(max, Math.max(0, Number(value) || 0));

const ITEM_CLIPBOARD_MIME = "application/x-created-items+json";
const ITEM_CLIPBOARD_PREFIX = "CREATED_ITEMS_V1:";

// Only copy item data; never carry the source work ID or server item ID.
const serializeCreatedItem = (item) => ({
  name: String(item.name ?? "").slice(0, FIELD_LIMITS.name),
  details: String(item.details ?? "").slice(0, FIELD_LIMITS.details),
  sizeArr: [...(item.sizeArr ?? parseSize(item.size))],
  cantType: item.cantType ?? "-",
  longCant: item.longCant ?? 0,
  shortCant: item.shortCant ?? 0,
  qty: item.qty ?? 1,
  rotable: item.rotable ?? true,
  rotation: item.rotation ?? 0,
  position: item.position ?? "",
  tablePosition: "",
  tableRotation: "",
  colorId: item.colorId ?? item.color?.colorId ?? null,
  millingId: item.millingId ?? item.milling?.millingId ?? null,
  millingVariantId: item.millingVariantId ?? "",
  object: item.object ?? null
});

const parseCreatedItemsClipboard = (clipboard) => {
  const custom = clipboard?.getData(ITEM_CLIPBOARD_MIME);
  const plain = clipboard?.getData("text/plain") ?? "";
  const raw =
    custom ||
    (plain.startsWith(ITEM_CLIPBOARD_PREFIX)
      ? plain.slice(ITEM_CLIPBOARD_PREFIX.length)
      : null);
  if (!raw) return null;
  try {
    const payload = JSON.parse(raw);
    if (
      payload?.type !== "created-items" ||
      payload.version !== 1 ||
      !Array.isArray(payload.rows) ||
      payload.rows.length === 0 ||
      payload.rows.length > 10000
    )
      return null;
    if (
      !payload.rows.every(
        (row) =>
          row &&
          typeof row === "object" &&
          !Array.isArray(row) &&
          Array.isArray(row.sizeArr)
      )
    )
      return null;
    return payload.rows;
  } catch {
    return null;
  }
};

/* ---- Sor-háttér színek (A THEME ELŐTT! hogy hivatkozható legyen) ---- */
const rowSelectedBg = "rgba(13,110,253,0.12)";
const rowActiveBg = "rgba(13,110,253,0.18)";
const rowHoverBg = "rgba(13,110,253,0.10)";
const rowHoverSelectedBg = "rgba(13,110,253,0.15)";
const cellActiveBg = "#3d8bfd";

/* ----------------- KÖZÖS TÉMA + STÍLUS HELPER ----------------- */
const THEME = {
  fontSize: 12,
  headerBg: "#f7f7f7",
  headerBorder: "#e5e5e5",
  cellBorder: "#eee",
  focusOutline: "#2b6ff5",
  rowHeight: 30,
  headerHeight: 26, // px – sticky top offset a gombhoz
  checkboxColW: "1.2fr",
  nrColW: "1.2fr",
  colorColW: "6fr",
  marasColW: "6fr",
  materialTypeColW: "3fr",
  nameColW: "6fr",
  lengthColW: "3fr",
  widthColW: "3fr",
  qtyColW: "1.5fr",
  rotableColW: "2fr",
  cantTypeColW: "2fr",
  longCantColW: "4fr",
  shortCantColW: "4fr",
  detailsColW: "6fr",
  deleteColW: "1.5rem",
  padHeader: "4px 6px",
  padCell: "0 6px",
  toolbarBg: "#ffffff",
  toolbarBorder: "#e5e5e5",
  radius: 10,
  colors: {
    rowSelectedBg,
    rowActiveBg,
    rowHoverBg,
    rowHoverSelectedBg,
    cellActiveBg
  }
};

const GRID_TEMPLATE = `${THEME.checkboxColW} ${THEME.nrColW} ${THEME.colorColW} ${THEME.marasColW} ${THEME.materialTypeColW} ${THEME.nameColW} ${THEME.lengthColW} ${THEME.widthColW} ${THEME.qtyColW} ${THEME.longCantColW} ${THEME.shortCantColW} ${THEME.cantTypeColW} ${THEME.rotableColW} ${THEME.detailsColW} ${THEME.deleteColW}`;

const toolbarStyle = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  border: `1px solid ${THEME.toolbarBorder}`,
  padding: "6px 10px",
  borderRadius: THEME.radius,
  background: THEME.toolbarBg,
  flexWrap: "nowrap",
  minWidth: 0,
  overflow: "hidden"
};

const COL = {
  SELECT: 0,
  COLOR: 1,
  MILLING: 2,
  NAME: 3,
  H: 4,
  W: 5,
  QTY: 6,
  LONG_CANT: 7,
  SHORT_CANT: 8,
  CANT_TYPE: 9,
  ROTABLE: 10,
  DETAILS: 11,
  DELETE: 12
};
const COLS = 13;

function btnStyle(variant = "ghost") {
  const base = {
    border: "1px solid transparent",
    cursor: "pointer",
    fontSize: 13,
    borderRadius: 999,
    padding: "6px 12px",
    transition: "transform .06s ease"
  };
  const map = {
    primary: { background: "#0d6efd", color: "#fff" },
    danger: { background: "#ef4444", color: "#fff" },
    ghost: {
      background: "#f3f4f6",
      color: "#111",
      border: "1px solid #e5e7eb"
    },
    muted: {
      background: "#eee",
      color: "#999",
      border: "1px solid #ddd",
      cursor: "not-allowed"
    }
  };
  return { ...base, ...(map[variant] || map.ghost) };
}

function computeCellBg({ activeCell, rowId, colIdx, selected, isHover }) {
  const isRowActive = activeCell?.rowId === rowId;

  if (isRowActive && colIdx >= 0 && activeCell?.colIdx === colIdx)
    return THEME.colors.cellActiveBg;
  if (isRowActive) return THEME.colors.rowActiveBg;
  if (selected && isHover) return THEME.colors.rowHoverSelectedBg;
  if (selected) return THEME.colors.rowSelectedBg;
  if (isHover) return THEME.colors.rowHoverBg;
  return "white";
}

function makeInputCellStyle({ bg, active, align = "left" }) {
  return {
    ...inpStyle,
    background: bg,
    color: active ? "#fff" : "#212529",
    borderRight: `1px solid ${THEME.cellBorder}`,
    borderLeft: `1px solid ${THEME.cellBorder}`,
    outline: active ? `2px solid ${THEME.focusOutline}` : "none",
    outlineOffset: -2,
    fontSize: THEME.fontSize,
    height: THEME.rowHeight,
    lineHeight: `${THEME.rowHeight - 4}px`,
    padding: THEME.padCell,
    margin: 0,
    textAlign: align
  };
}

function makeIconDeleteButtonStyle() {
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    width: 24,
    height: THEME.rowHeight - 8,
    background: "none",
    border: "none",
    color: "#ef4444",
    cursor: "pointer"
  };
}

/* ----------------- Stílus helper ----------------- */

const inpStyle = {
  width: "100%",
  padding: "6px",
  height: "2rem",
  border: "none",
  fontSize: 13
};

const pillSelectStyle = {
  ...inpStyle,
  width: 240,
  background: "#f9fafb",
  border: "1px solid #e5e7eb",
  borderRadius: 999,
  padding: "6px 12px",
  fontSize: 13
};

/* ----------------- Fő oldal ----------------- */

export default function CreatedItemsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [hasDirty, setHasDirty] = useState(false);
  const [showLeaveGuard, setShowLeaveGuard] = useState(false);
  const leaveDialogRef = useRef(null);
  const cancelLeaveRef = useRef(null);
  const leaveGuardOpenRef = useRef(false);
  leaveGuardOpenRef.current = showLeaveGuard;
  useLayoutEffect(() => {
    if (!showLeaveGuard) return;
    const focusCancel = () => cancelLeaveRef.current?.focus();
    const containFocus = (event) => {
      if (!leaveDialogRef.current?.contains(event.target)) focusCancel();
    };
    const handleDialogKey = (event) => {
      event.stopImmediatePropagation();
      const buttons = Array.from(
        leaveDialogRef.current?.querySelectorAll("button:not(:disabled)") || []
      );
      if (
        ["Tab", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(
          event.key
        )
      ) {
        event.preventDefault();
        const index = buttons.indexOf(document.activeElement);
        const backwards =
          event.key === "ArrowLeft" ||
          event.key === "ArrowUp" ||
          (event.key === "Tab" && event.shiftKey);
        buttons[
          (index + (backwards ? -1 : 1) + buttons.length) % buttons.length
        ]?.focus();
      } else if (event.key === "Escape") {
        event.preventDefault();
        setShowLeaveGuard(false);
      } else if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        if (!event.repeat && buttons.includes(document.activeElement))
          document.activeElement.click();
      }
    };
    focusCancel();
    document.addEventListener("focusin", containFocus);
    document.addEventListener("keydown", handleDialogKey, true);
    return () => {
      document.removeEventListener("focusin", containFocus);
      document.removeEventListener("keydown", handleDialogKey, true);
      if (addButtonRef.current?.isConnected) addButtonRef.current.focus();
    };
  }, [showLeaveGuard]);
  const [pendingPath, setPendingPath] = useState(null);
  const { workId } = useParams();
  const workIdNum = Number(workId);
  const { t } = useLanguage();
  const wrapperRef = useRef(null);
  const addButtonRef = useRef(null);
  const listRef = useRef(null);
  const dragRowsRef = useRef(null);
  const suppressDragClickRef = useRef(false);
  const headerRef = useRef(null);
  const navLockRef = useRef(false);
  const scrollRAF = useRef(0);
  const [paletteToSelection, setPaletteToSelection] = useState(false);
  const [pendingDeletes, setPendingDeletes] = useState(new Set());
  const allMillings = useSelector((state) => state.millings || []);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const isSelectedMulti = useCallback(
    (id) => selectedIds.has(id),
    [selectedIds]
  );
  const pendingFocusRef = useRef(null);
  const saveFocusRef = useRef(null);
  const [cellRange, setCellRange] = useState(null);
  const clearAllSelection = useCallback(() => {
    setCellRange(null);
    setSelectedIds(new Set());
    setSelectedItemId(null);
    setActiveCell({ rowId: null, colIdx: null });
  }, []);
  useEffect(() => {
    window.history.pushState({ __guard: true }, "", window.location.href);
  }, []);
  useEffect(() => {
    try {
      dispatch(getAllColors());
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [showPaletteModal, setShowPaletteModal] = useState(false);
  const [swapSourceColor, setSwapSourceColor] = useState(undefined);

  const allColors = useSelector(
    (state) =>
      Array.isArray(state.colors)
        ? state.colors
        : Object.values(state.colors || {}),
    shallowEqual
  );
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") clearAllSelection();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [clearAllSelection]);

  const { listForWork } = useSelector(
    (state) => {
      const raw = state.createdItems;
      const list = Array.isArray(raw) ? raw : Object.values(raw || {});
      const filtered = list.filter((it) => it?.work?.workId === workIdNum);

      const stamp = filtered
        .map(
          (x) =>
            `${x.itemId}|${x.size}|${x.kant}|${x.qty}|${x.details ?? ""}|${
              x.rotable ? 1 : 0
            }|${x.colorId ?? x.color?.colorId ?? ""}|${x.rotation ?? ""}|${x.milling?.millingId ?? ""}|${x.millingVariantId ?? ""}`
        )
        .join(";");

      return { listForWork: filtered, stamp };
    },
    (a, b) => a.stamp === b.stamp
  );

  const createdItemsFromStore = listForWork;

  const currentUser = useSelector(
    (s) => s.auth?.user || s.currentUser || s.user || null
  );
  const workEntityFromStore = useSelector(
    (s) =>
      s.works?.byId?.[workIdNum] ||
      (Array.isArray(s.works)
        ? s.works.find((w) => w?.workId === workIdNum)
        : null)
  );
  const workEntity =
    workEntityFromStore || createdItemsFromStore?.[0]?.work || null;

  const role = String(currentUser?.role || "").toLowerCase();
  const isOrdered = !!workEntity?.isOrdered;
  const companyStatus = workEntity?.companyStatus;
  const isLocked =
    (role === "user" && isOrdered) ||
    (role !== "user" && isOrdered && companyStatus === "Completed");

  useEffect(() => {
    if (isLocked) return;
    let interacted = false;
    let frame;
    const focusAddButton = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (
          !interacted &&
          !leaveGuardOpenRef.current &&
          document.visibilityState !== "hidden"
        ) {
          addButtonRef.current?.focus({ preventScroll: true });
        }
      });
    };
    const stopInitialFocus = () => {
      interacted = true;
      cancelAnimationFrame(frame);
    };
    const handleFocusChange = (event) => {
      if (event.target !== addButtonRef.current) focusAddButton();
    };
    focusAddButton();
    window.addEventListener("focus", focusAddButton);
    document.addEventListener("visibilitychange", focusAddButton);
    document.addEventListener("focusin", handleFocusChange);
    document.addEventListener("pointerdown", stopInitialFocus, true);
    document.addEventListener("keydown", stopInitialFocus, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("focus", focusAddButton);
      document.removeEventListener("visibilitychange", focusAddButton);
      document.removeEventListener("focusin", handleFocusChange);
      document.removeEventListener("pointerdown", stopInitialFocus, true);
      document.removeEventListener("keydown", stopInitialFocus, true);
    };
  }, [workIdNum, isLocked]);

  useEffect(() => {
    console.log("createdItemsFromStore changed:", createdItemsFromStore);
  }, [createdItemsFromStore]);

  const [rawItems, setItems] = useState([]);
  // Clear hidden PFL fields for loaded, pasted and bulk-edited rows too.
  const normalizeMaterialFields = useCallback((item) => {
    const colorId = item.colorId ?? item.color?.colorId;
    const materialType = allColors.find((color) => color.colorId === colorId)?.materialType
      ?? item.color?.materialType;
    return materialType === "PFL" && (item.rotable !== false || item.rotation !== 0 ||
      item.cantType !== "-" || item.longCant !== 0 || item.shortCant !== 0 || item.kant !== buildKantString("-", 0, 0))
      ? { ...item, rotable: false, rotation: 0, cantType: "-", longCant: 0, shortCant: 0, kant: buildKantString("-", 0, 0) }
      : item;
  }, [allColors]);
  const items = useMemo(() => rawItems.map(normalizeMaterialFields), [rawItems, normalizeMaterialFields]);
  useEffect(() => {
    // Persist resets so switching away from PFL cannot restore old hidden values.
    if (items.some((item, index) => item !== rawItems[index])) setItems(items);
  }, [items, rawItems]);
  const [historyAvailability, setHistoryAvailability] = useState({
    undo: false,
    redo: false
  });
  const historyRef = useRef({ past: [], present: null, future: [] });
  const savingRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => {
    const blockWhileSaving = (event) => {
      if (!savingRef.current) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    const events = ["keydown", "keyup", "click", "dblclick", "pointerdown",
      "mousedown", "touchstart", "wheel", "copy", "cut", "paste",
      "beforeinput", "input", "change", "submit", "dragstart", "contextmenu"];
    const options = { capture: true, passive: false };
    events.forEach((name) => window.addEventListener(name, blockWhileSaving, options));
    return () => events.forEach((name) => window.removeEventListener(name, blockWhileSaving, options));
  }, []);
  const resetHistory = useCallback((nextItems, deletes = new Set()) => {
    setHistoryAvailability({ undo: false, redo: false });
    historyRef.current = {
      past: [],
      present: { items: nextItems, pendingDeletes: deletes },
      future: []
    };
  }, []);
  useLayoutEffect(() => {
    if (savingRef.current) return;
    const history = historyRef.current;
    const next = { items, pendingDeletes };
    const previous = history.present;
    if (
      previous &&
      JSON.stringify(previous.items) === JSON.stringify(items) &&
      previous.pendingDeletes.size === pendingDeletes.size &&
      [...pendingDeletes].every((id) => previous.pendingDeletes.has(id))
    )
      return;
    if (previous) history.past = [...history.past.slice(-99), previous];
    history.present = next;
    history.future = [];
    setHistoryAvailability({ undo: history.past.length > 0, redo: false });
  }, [items, pendingDeletes]);
  const nextPasteIdRef = useRef(-Date.now() - 1000000);
  const originalRef = useRef([]);
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!hasDirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasDirty]);

  useEffect(() => {
    const onPopState = (e) => {
      if (!hasDirty || showLeaveGuard) return;
      window.history.pushState(null, "", window.location.href);
      setPendingPath(-1);
      setShowLeaveGuard(true);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [hasDirty, showLeaveGuard]);
  const { registerGuard } = useNavigationGuard();

  useEffect(() => {
    const unregister = registerGuard(async (targetPath) => {
      if (!hasDirty) return true;

      setPendingPath(targetPath);
      setShowLeaveGuard(true);
      return false;
    });

    return unregister;
  }, [hasDirty, registerGuard]);
  useEffect(() => {
    const normalized = (createdItemsFromStore || []).map((it, idx) => {
      const [h, w, t] = parseSize(it.size);
      const [cantType, longCant, shortCant] = parseKant(it.kant);
      return {
        __idx: idx,
        ...it,
        name: it.name ?? "",
        sizeArr: [h, w, t],
        cantType,
        longCant,
        shortCant,
        rotable: it.rotable ?? true,
        rotation: it.rotation ?? null,
        colorId: it.colorId ?? it.color?.colorId ?? null,
        millingId: it.milling?.millingId ?? null
      };
    });

    // Mentés közben az API frissítheti a Redux-listát, ami újrarendereli a sorokat.
    // A korábban aktív cellát a frissen betöltött soron is állítsuk vissza.
    if (saveFocusRef.current) {
      const snapshot = saveFocusRef.current;
      const savedRow =
        normalized.find((it) => it.itemId === snapshot.rowId) ??
        normalized[snapshot.rowIndex];
      pendingFocusRef.current = {
        rowId: savedRow?.itemId ?? null,
        colIdx: snapshot.colIdx,
        selectText: snapshot.selectText
      };
    }
    setItems(normalized);
    if (!savingRef.current) {
      setPendingDeletes(new Set());
      resetHistory(normalized);
    }
    originalRef.current = normalized.map((x) => ({ ...x }));
    setHasDirty(false);
  }, [createdItemsFromStore, resetHistory]);

  const palette = useMemo(() => {
    const ids = Array.from(
      new Set(
        (items || [])
          .map((it) => it.colorId ?? it.color?.colorId)
          .filter((x) => x != null)
      )
    );
    return ids
      .map((id) => allColors.find((c) => c.colorId === id))
      .filter(Boolean);
  }, [items, allColors]);

  const [selectedColorId, setSelectedColorId] = useState(null);
  const [noColorOnly, setNoColorOnly] = useState(false);
  const visibleItems = useMemo(() => {
    const base = items.filter((it) => !pendingDeletes.has(it.itemId));

    if (noColorOnly) {
      return base.filter((it) => (it.colorId ?? it.color?.colorId) == null);
    }

    return selectedColorId == null
      ? base
      : base.filter(
          (it) => (it.colorId ?? it.color?.colorId) === selectedColorId
        );
  }, [items, selectedColorId, noColorOnly, pendingDeletes]);

  // Egérrel történő sor-kijelölés, a böngésző szövegkijelölése helyett.
  const beginRowDrag = useCallback(
    (event) => {
      if (isLocked || event.button !== 0 || !event.isPrimary) return;
      if (showLeaveGuard || showPaletteModal) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const row = target.closest('[data-row="1"]');
      if (!row || !listRef.current?.contains(row)) return;
      // A checkbox, a törlés és a legördülő menük továbbra is kattinthatók.
      if (
        target.closest(
          'input[type="checkbox"], [data-no-row-drag], .modal, [role="dialog"]'
        )
      )
        return;

      suppressDragClickRef.current = false;
      dragRowsRef.current = {
        pointerId: event.pointerId,
        startId: Number(row.dataset.rowId),
        startX: event.clientX,
        startY: event.clientY,
        dragging: false
      };
    },
    [isLocked, showLeaveGuard, showPaletteModal]
  );

  useEffect(() => {
    const move = (event) => {
      const drag = dragRowsRef.current;
      if (!drag || event.pointerId !== drag.pointerId) return;
      if (
        !drag.dragging &&
        Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) < 5
      )
        return;
      drag.dragging = true;
      suppressDragClickRef.current = true;
      window.getSelection()?.removeAllRanges();
      event.preventDefault();
      const row = document
        .elementFromPoint(event.clientX, event.clientY)
        ?.closest?.('[data-row="1"]');
      if (!row || !listRef.current?.contains(row)) return;
      const start = visibleItems.findIndex(
        (item) => item.itemId === drag.startId
      );
      const end = visibleItems.findIndex(
        (item) => item.itemId === Number(row.dataset.rowId)
      );
      if (start < 0 || end < 0) return;
      const ids = visibleItems
        .slice(Math.min(start, end), Math.max(start, end) + 1)
        .map((item) => item.itemId);
      setSelectedIds(new Set(ids));
      setCellRange(null);
    };
    const end = (event) => {
      if (dragRowsRef.current?.pointerId !== event.pointerId) return;
      dragRowsRef.current = null;
      window.getSelection()?.removeAllRanges();
    };
    const preventTextSelection = (event) => {
      if (dragRowsRef.current?.dragging) event.preventDefault();
    };
    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
    document.addEventListener("selectstart", preventTextSelection, true);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
      document.removeEventListener("selectstart", preventTextSelection, true);
    };
  }, [visibleItems]);

  const allVisibleSelected =
    visibleItems.length > 0 &&
    visibleItems.every((i) => selectedIds.has(i.itemId));

  useEffect(() => {
    if (!pendingFocusRef.current) return;

    const { rowId, colIdx, selectText } = pendingFocusRef.current;
    // Ha a törlés után nincs megjeleníthető sor, a hozzáadás gombra lépünk.
    if (rowId === null && visibleItems.length === 0) {
      requestAnimationFrame(() => {
        addButtonRef.current?.focus({ preventScroll: true });
        setSelectedItemId(null);
        setActiveCell({ rowId: null, colIdx: null });
        navLockRef.current = false;
        pendingFocusRef.current = null;
      });
      return;
    }

    const rowIdx = visibleItems.findIndex((it) => it.itemId === rowId);

    if (rowIdx === -1) return;

    requestAnimationFrame(() => {
      focusCell(rowIdx, colIdx);
      if (selectText) gridRefs.current?.[rowIdx]?.[colIdx]?.current?.select?.();
      setSelectedItemId(rowId);
      setActiveCell({ rowId, colIdx });
      navLockRef.current = false;
      pendingFocusRef.current = null;
    });
  }, [visibleItems]);

  const gridRefs = useRef([]);

  const [selectedItemId, setSelectedItemId] = useState(null);
  const [activeCell, setActiveCell] = useState({ rowId: null, colIdx: null });
  const [editingCell, setEditingCell] = useState(null);

  const handleBlankClick = useCallback(
    (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;

      // Csak valóban üres felületre kattintva töröljük a kijelölést.
      // A sorok és a kezelőelemek (gombok, szűrők, inputok) nem számítanak üresnek.
      if (
        target.closest(
          '[data-row="1"], tbody tr, button, input, textarea, select, a, ' +
            '[role="button"], [role="dialog"], .modal, label'
        )
      )
        return;

      clearAllSelection();
      setEditingCell(null);
      lastSelectedRef.current = null;
      pendingFocusRef.current = null;
    },
    [clearAllSelection]
  );

  const ensureRowFullyVisible = (rowEl) => {
    const sc = listRef.current;
    if (!sc || !rowEl) return;
    const pad = 2;
    const adjustOnce = () => {
      const scRect = sc.getBoundingClientRect();
      const hdrRect = headerRef.current?.getBoundingClientRect() || null;
      const visibleTop =
        Math.max(scRect.top, hdrRect ? hdrRect.bottom : scRect.top) + pad;
      const visibleBottom = scRect.bottom - pad;
      const rowRect = rowEl.getBoundingClientRect();
      if (rowRect.top < visibleTop) {
        sc.scrollTop -= visibleTop - rowRect.top;
        return true;
      }
      if (rowRect.bottom > visibleBottom) {
        sc.scrollTop += rowRect.bottom - visibleBottom;
        return true;
      }
      return false;
    };
    const moved = adjustOnce();
    if (moved) {
      requestAnimationFrame(() => {
        adjustOnce();
      });
    }
  };

  const onRowActivate = useCallback(
    (e, rowId) => {
      if (isLocked) return;
      setSelectedItemId(rowId);
    },
    [isLocked]
  );

  const focusCell = (rowIdx, colIdx) => {
    const r = Math.max(0, Math.min(rowIdx, visibleItems.length - 1));
    const c = Math.max(0, Math.min(colIdx, COLS - 1));
    const ref =
      gridRefs.current?.[r]?.[c] ||
      (gridRefs.current[r] && gridRefs.current[r][c]);
    const el = ref?.current;
    if (!el) return;
    el.focus({ preventScroll: true });
    if (scrollRAF.current) cancelAnimationFrame(scrollRAF.current);
    scrollRAF.current = requestAnimationFrame(() => {
      const rowEl = el.closest('[data-row="1"]') || el.parentElement;
      ensureRowFullyVisible(rowEl);
    });
  };

  // Csak a még nem mentett, alapértékeken hagyott sorokat hagyjuk ki.
  // Az új sor színe és marása öröklődhet, ez önmagában nem számít kitöltésnek.
  const isDefaultEmptyNewItem = (item) => {
    if (Number(item.itemId) >= 0) return false;
    const [length, width] = item.sizeArr ?? parseSize(item.size);
    return (
      !Number(length) &&
      !Number(width) &&
      !String(item.name ?? "").trim() &&
      !String(item.details ?? "").trim() &&
      Number(item.qty ?? 1) === 1 &&
      (item.cantType ?? "-") === "-" &&
      !Number(item.longCant ?? 0) &&
      !Number(item.shortCant ?? 0) &&
      (item.rotable ?? true) === true &&
      !Number(item.rotation ?? 0) &&
      !String(item.position ?? "").trim()
    );
  };

  const saveAll = useCallback(async () => {
    if (isLocked || savingRef.current) return;

    // Az aktív cellát a mentés előtt mentjük, nem a mentésgomb fókuszát.
    const rowIndex = visibleItems.findIndex(
      (it) => it.itemId === activeCell.rowId
    );
    saveFocusRef.current =
      rowIndex >= 0
        ? {
            rowId: activeCell.rowId,
            rowIndex,
            colIdx: activeCell.colIdx,
            selectText: true
          }
        : null;

    // A törölt és a teljesen üres új sorok nem kerülnek mentésre.
    const retainedItems = items.filter(
      (it) => !pendingDeletes.has(it.itemId) && !isDefaultEmptyNewItem(it)
    );
    const changed = diffChangedItems(originalRef.current.map(normalizeMaterialFields), retainedItems);
    const deletedIds = [...pendingDeletes].filter((id) => Number(id) > 0);

    // Ha csak mentetlen új sorokat töröltünk, nincs szerveroldali művelet.
    if (!changed.length && !deletedIds.length) {
      originalRef.current = retainedItems.map((item) => ({ ...item }));
      resetHistory(retainedItems);
      setItems(retainedItems);
      setPendingDeletes(new Set());
      setHasDirty(false);
      saveFocusRef.current = null;
      return;
    }

    const newItems = changed.filter((it) => it.__isNew);
    const updatedItems = changed.filter(
      (it) => !it.__isNew && typeof it.itemId === "number" && it.itemId > 0
    );

    const createPayloads = newItems.map(buildItemUpdatePayload);

    const updatePayloads = updatedItems.map((it) => ({
      itemId: it.itemId,
      ...buildItemUpdatePayload(it),
      tablePosition: it.tablePosition ?? ""
    }));

    const deleteServerIds = deletedIds;

    savingRef.current = true;
    setIsSaving(true);
    try {
      if (createPayloads.length) {
        await dispatch(
          createdItemApi.createMultipleCreatedItemsApi(createPayloads)
        );
      }

      if (updatePayloads.length) {
        await dispatch(
          createdItemApi.updateMultipleCreatedItemsApi(updatePayloads, {
            requiresRegenerate: false
          })
        );
      }

      if (deleteServerIds.length) {
        await dispatch(
          createdItemApi.deleteMultipleCreatedItemsApi(deleteServerIds)
        );
      }

      originalRef.current = retainedItems.map((item) => ({ ...item }));
      setItems(retainedItems);
      resetHistory(retainedItems);

      setPendingDeletes(new Set());
      setHasDirty(false);

      alert(t("saveSuccess"));
      // Az alert bezárásakor a böngésző fókusza visszakerülhet a mentésgombra.
      // Ezért csak utána fókuszáljuk ismét a cellát.
      const savedFocus = saveFocusRef.current;
      if (savedFocus) {
        requestAnimationFrame(() => {
          const currentRows = Array.from(
            listRef.current?.querySelectorAll('[data-row="1"]') ?? []
          );
          const targetIndex = currentRows.findIndex(
            (el) => String(el.dataset.rowId) === String(savedFocus.rowId)
          );
          const idx =
            targetIndex >= 0
              ? targetIndex
              : Math.min(savedFocus.rowIndex, currentRows.length - 1);
          if (idx >= 0) {
            const el = gridRefs.current?.[idx]?.[savedFocus.colIdx]?.current;
            el?.focus({ preventScroll: true });
            if (savedFocus.selectText) el?.select?.();
            const realId = Number(currentRows[idx]?.dataset.rowId);
            setSelectedItemId(realId);
            setActiveCell({ rowId: realId, colIdx: savedFocus.colIdx });
          } else {
            addButtonRef.current?.focus({ preventScroll: true });
          }
          saveFocusRef.current = null;
        });
      }
    } catch (err) {
      saveFocusRef.current = null;
      console.error("[saveAll] error:", err);
      alert(t("saveFailed"));
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }, [
    isLocked,
    items,
    pendingDeletes,
    dispatch,
    t,
    visibleItems,
    activeCell,
    resetHistory,
    normalizeMaterialFields
  ]);

  useEffect(() => {
    if (pendingDeletes.size > 0) {
      setHasDirty(true);
      return;
    }
    // Apply the same display rules to the saved baseline. Loading a PFL row
    // (or receiving its color metadata later) is not a user edit.
    const orig = originalRef.current.map(normalizeMaterialFields);
    const cur = items;
    if (orig.length !== cur.length) {
      setHasDirty(true);
      return;
    }
    const changed = diffChangedItems(orig, cur);
    setHasDirty(changed.length > 0);
  }, [items, pendingDeletes, normalizeMaterialFields]);

  useEffect(() => {
    if (!items.length) return;
    if (noColorOnly) return;
    if (selectedColorId == null) return;

    const stillHasSelected = items.some(
      (it) => (it.colorId ?? it.color?.colorId) === selectedColorId
    );

    if (!stillHasSelected) {
      const firstColor = palette[0]?.colorId;
      if (firstColor != null) {
        setSelectedColorId(firstColor);
      } else {
        setSelectedColorId(null);
        setNoColorOnly(true);
      }
    }
  }, [items, palette, selectedColorId, noColorOnly]);

  const openPaletteForCurrent = () => {
    if (isLocked) return;
    setPaletteToSelection(selectedIds.size > 0);
    setSwapSourceColor(noColorOnly ? null : (selectedColorId ?? null));
    setShowPaletteModal(true);
  };

  const applyBulkPatch = (patch) => {
    if (isLocked) return;
    if (paletteToSelection && selectedIds.size) {
      const ids = new Set(selectedIds);
      setItems((prev) =>
        prev.map((it) => (ids.has(it.itemId) ? { ...it, ...patch } : it))
      );
    } else {
      const src = swapSourceColor ?? null;
      setItems((prev) =>
        prev.map((it) => {
          const cur = it.colorId ?? it.color?.colorId ?? null;
          const match = src === null ? cur == null : cur === src;
          return match ? { ...it, ...patch } : it;
        })
      );
    }
    closePalette();
  };

  const closePalette = () => {
    setShowPaletteModal(false);
    setSwapSourceColor(undefined);
  };

  const lastSelectedRef = useRef(null); // utolsó (de)select-elt sor itemId-ja

  const toggleRowSelected = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    lastSelectedRef.current = id;
  }, []);

  // Shift+klikk range kijelölés
  const handleRowSelect = useCallback(
    (id, e) => {
      const shiftHeld = e?.shiftKey;

      if (
        shiftHeld &&
        lastSelectedRef.current != null &&
        lastSelectedRef.current !== id
      ) {
        const lastId = lastSelectedRef.current;
        const idxA = visibleItems.findIndex((it) => it.itemId === lastId);
        const idxB = visibleItems.findIndex((it) => it.itemId === id);
        if (idxA !== -1 && idxB !== -1) {
          const from = Math.min(idxA, idxB);
          const to = Math.max(idxA, idxB);
          const rangeIds = visibleItems
            .slice(from, to + 1)
            .map((it) => it.itemId);
          // a range iránya: az utolsó elem státusza dönti el (select/deselect)
          const targetState = !selectedIds.has(id);
          setSelectedIds((prev) => {
            const next = new Set(prev);
            rangeIds.forEach((rid) => {
              if (targetState) next.add(rid);
              else next.delete(rid);
            });
            return next;
          });
          lastSelectedRef.current = id;
          return;
        }
      }

      // normál toggle
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      lastSelectedRef.current = id;
    },
    [visibleItems, selectedIds]
  );

  const toggleSelectAllVisible = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected =
        visibleItems.length > 0 &&
        visibleItems.every((i) => next.has(i.itemId));
      if (allSelected) {
        visibleItems.forEach((i) => next.delete(i.itemId));
      } else {
        visibleItems.forEach((i) => next.add(i.itemId));
      }
      return next;
    });
  }, [visibleItems]);

  // A törlendő sorok fölötti első megmaradó sor hosszméret-celláját
  // fókuszáljuk. Ha nem marad sor, az "Új elem hozzáadása" gombot.
  const prepareFocusAfterDelete = useCallback(
    (idsToDelete) => {
      const deleted = new Set(idsToDelete);
      const firstDeletedIndex = visibleItems.findIndex((it) =>
        deleted.has(it.itemId)
      );
      if (firstDeletedIndex === -1) return;

      const precedingRow = visibleItems
        .slice(0, firstDeletedIndex)
        .reverse()
        .find((it) => !deleted.has(it.itemId));
      const nextRow = visibleItems.find((it) => !deleted.has(it.itemId));
      const target = precedingRow ?? nextRow;

      pendingFocusRef.current = {
        rowId: target?.itemId ?? null,
        colIdx: COL.H,
        selectText: true
      };
      setCellRange(null);
      setSelectedItemId(target?.itemId ?? null);
      setActiveCell({
        rowId: target?.itemId ?? null,
        colIdx: target ? COL.H : null
      });
    },
    [visibleItems]
  );

  const markDeleted = useCallback(
    (id) => {
      if (isLocked) return;
      if (!window.confirm(t("deleteItemConfirm"))) return;

      prepareFocusAfterDelete([id]);
      setPendingDeletes((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setHasDirty(true);
    },
    [isLocked, t, prepareFocusAfterDelete]
  );

  // Checkboxos sorok + Shift+nyíllal kijelölt cellák sorai.
  const rowsToDelete = useMemo(() => {
    const ids = new Set(selectedIds);
    cellRange?.ids?.forEach((id) => ids.add(id));
    return visibleItems
      .filter((item) => ids.has(item.itemId))
      .map((item) => item.itemId);
  }, [selectedIds, cellRange, visibleItems]);

  const deleteSelected = useCallback(() => {
    if (isLocked || rowsToDelete.length === 0) return;
    if (
      !window.confirm(
        t("deleteSelectedConfirm", { count: rowsToDelete.length })
      )
    )
      return;

    prepareFocusAfterDelete(rowsToDelete);
    setPendingDeletes((prev) => {
      const next = new Set(prev);
      rowsToDelete.forEach((id) => next.add(id));
      return next;
    });
    setSelectedIds(new Set());
    setCellRange(null);
    setHasDirty(true);
  }, [isLocked, rowsToDelete, t, prepareFocusAfterDelete]);

  useEffect(() => {
    const onKey = (e) => {
      if (
        e.key !== "Delete" ||
        isLocked ||
        showLeaveGuard ||
        e.target.closest?.('[role="dialog"], .modal') ||
        !wrapperRef.current?.contains(e.target)
      )
        return;
      const rowElement = e.target.closest?.('[data-row="1"]');
      if (
        !rowElement &&
        e.target.closest?.("input, select, textarea, [contenteditable=true]")
      )
        return;
      if (!rowElement && rowsToDelete.length === 0) return;
      e.preventDefault();
      if (e.repeat) return;
      if (rowsToDelete.length > 0) deleteSelected();
      else markDeleted(Number(rowElement.dataset.rowId));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [rowsToDelete, isLocked, showLeaveGuard, deleteSelected, markDeleted]);

  const withSelected = useCallback(
    (mutator) => {
      if (isLocked || selectedIds.size === 0) return;
      const ids = new Set(selectedIds);
      setItems((prev) =>
        prev.map((it) => (ids.has(it.itemId) ? mutator(it) : it))
      );
    },
    [isLocked, selectedIds]
  );

  const bulkSetRotable = useCallback(
    (val) => withSelected((it) => ({ ...it, rotable: !!val })),
    [withSelected]
  );

  const bulkSetCantType = useCallback(
    (ct) =>
      withSelected((it) => {
        const norm = normalizeCantTypeInput(ct);
        return {
          ...it,
          cantType: norm,
          kant: buildKantString(norm, it.longCant ?? 0, it.shortCant ?? 0)
        };
      }),
    [withSelected]
  );

  const bulkSetLongShort = useCallback(
    (which, val) =>
      withSelected((it) => {
        const n = Number(val) || 0;
        const longCant = which === "long" ? n : (it.longCant ?? 0);
        const shortCant = which === "short" ? n : (it.shortCant ?? 0);
        return {
          ...it,
          longCant,
          shortCant,
          kant: buildKantString(it.cantType ?? "-", longCant, shortCant)
        };
      }),
    [withSelected]
  );

  const bulkSetQty = useCallback(
    (qty) =>
      withSelected((it) => ({
        ...it,
        qty: clampNumber(qty, FIELD_LIMITS.qty)
      })),
    [withSelected]
  );

  const focusEmptyDraft = useCallback(() => {
    const emptyDraft = items.find(
      (item) =>
        item.itemId < 0 &&
        !pendingDeletes.has(item.itemId) &&
        !item.sizeArr[0] &&
        !item.sizeArr[1]
    );
    if (!emptyDraft) return false;
    pendingFocusRef.current = {
      rowId: emptyDraft.itemId,
      colIdx: COL.H,
      selectText: true
    };
    if (!visibleItems.some((item) => item.itemId === emptyDraft.itemId)) {
      setSelectedColorId(null);
      setNoColorOnly(false);
    }
    setItems((prev) => [...prev]);
    return true;
  }, [items, pendingDeletes, visibleItems]);

  // System clipboard: copy and paste rows across work pages (even after navigation).
  useEffect(() => {
    const isOnPage = (target) =>
      target instanceof Element &&
      (wrapperRef.current?.contains(target) ||
        target === document.body ||
        target === document.documentElement);

    const onCopy = (event) => {
      if (!isOnPage(event.target) || showLeaveGuard || showPaletteModal) return;
      if (
        event.target.closest?.(
          '[role="dialog"], .modal, [contenteditable="true"]'
        )
      )
        return;
      // An editable field owns copy even when rows are still selected.
      if (
        listRef.current?.contains(event.target) &&
        ((event.target instanceof HTMLInputElement && ["text", "number"].includes(event.target.type)) || event.target instanceof HTMLTextAreaElement) &&
        !event.target.readOnly
      ) return;
      const ids = new Set(selectedIds);
      cellRange?.ids?.forEach((id) => ids.add(id));
      const hasExplicitSelection = ids.size > 0;
      if (!hasExplicitSelection) {
        const el = event.target;
        const hasTextSelection =
          (el instanceof HTMLInputElement ||
            el instanceof HTMLTextAreaElement) &&
          el.selectionStart != null &&
          el.selectionEnd > el.selectionStart;
        if (hasTextSelection) return;
        if (activeCell.rowId != null) ids.add(activeCell.rowId);
      }
      if (!ids.size) return;
      const rows = visibleItems.filter((item) => ids.has(item.itemId));
      if (!rows.length) return;
      const payload = JSON.stringify({
        type: "created-items",
        version: 1,
        rows: rows.map(serializeCreatedItem)
      });
      if (!event.clipboardData) return;
      event.clipboardData.setData(ITEM_CLIPBOARD_MIME, payload);
      event.clipboardData.setData(
        "text/plain",
        ITEM_CLIPBOARD_PREFIX + payload
      );
      event.preventDefault();
    };

    const onPaste = (event) => {
      if (
        isLocked ||
        showLeaveGuard ||
        showPaletteModal ||
        !isOnPage(event.target)
      )
        return;
      if (
        event.target.closest?.(
          '[role="dialog"], .modal, [contenteditable="true"]'
        )
      )
        return;
      // Keep filters, search boxes, and other toolbar editors' native paste behavior.
      const insideGrid = listRef.current?.contains(event.target);
      const isBackground =
        event.target === document.body ||
        event.target === document.documentElement;
      if (!insideGrid && !isBackground && event.target !== addButtonRef.current)
        return;
      const rows = parseCreatedItemsClipboard(event.clipboardData);
      if (!rows) return; // Normal text pasting is still supported.
      event.preventDefault();
      event.stopPropagation();
      setEditingCell(null);
      const copies = rows.map((row) => {
        const size = [
          clampNumber(row.sizeArr[0], FIELD_LIMITS.length),
          clampNumber(row.sizeArr[1], FIELD_LIMITS.width),
          clampNumber(row.sizeArr[2], 10000)
        ];
        const cantType = normalizeCantTypeInput(row.cantType ?? "-");
        const longCant = clampNumber(row.longCant, 2);
        const shortCant = clampNumber(row.shortCant, 2);
        return {
          itemId: nextPasteIdRef.current--,
          __isNew: true,
          work: { workId: workIdNum },
          name: String(row.name ?? "").slice(0, FIELD_LIMITS.name),
          details: String(row.details ?? "").slice(0, FIELD_LIMITS.details),
          sizeArr: size,
          size: buildSizeString(...size),
          cantType,
          longCant,
          shortCant,
          kant: buildKantString(cantType, longCant, shortCant),
          qty: clampNumber(row.qty ?? 1, FIELD_LIMITS.qty),
          rotable: !!row.rotable,
          rotation: row.rotation ?? 0,
          position: row.position ?? "",
          tablePosition: "",
          tableRotation: "",
          table: null,
          colorId: row.colorId ?? null,
          millingId: row.millingId ?? null,
          millingVariantId: row.millingVariantId ?? "",
          object: null
        };
      });
      const lastCopy = copies[copies.length - 1];
      pendingFocusRef.current = {
        rowId: lastCopy.itemId,
        colIdx: COL.H,
        selectText: true
      };
      setSelectedColorId(null);
      setNoColorOnly(false);
      setCellRange(null);
      setSelectedIds(new Set(copies.map((row) => row.itemId)));
      setSelectedItemId(lastCopy.itemId);
      setItems((prev) => [...prev, ...copies]);
      setHasDirty(true);
      requestAnimationFrame(() => {
        if (listRef.current)
          listRef.current.scrollTop = listRef.current.scrollHeight;
      });
    };

    window.addEventListener("copy", onCopy, true);
    window.addEventListener("paste", onPaste, true);
    return () => {
      window.removeEventListener("copy", onCopy, true);
      window.removeEventListener("paste", onPaste, true);
    };
  }, [
    visibleItems,
    selectedIds,
    cellRange,
    activeCell.rowId,
    workIdNum,
    isLocked,
    showLeaveGuard,
    showPaletteModal
  ]);

  const navigateHistory = useCallback(
    (direction) => {
      if (isLocked || savingRef.current) return;
      const history = historyRef.current;
      const source = direction === "undo" ? history.past : history.future;
      if (!source.length) return;
      const destination = direction === "undo" ? history.future : history.past;
      destination.push(history.present);
      const snapshot = source.pop();
      history.present = snapshot;
      setItems(snapshot.items);
      setPendingDeletes(snapshot.pendingDeletes);
      setSelectedIds(new Set());
      setCellRange(null);
      setEditingCell(null);
      setHistoryAvailability({
        undo: history.past.length > 0,
        redo: history.future.length > 0
      });
    },
    [isLocked]
  );

  // Ctrl+A selects rows, Ctrl+S saves. Prevent OS key-repeat from
  // repeatedly pasting (or corrupting a focused number input).
  useEffect(() => {
    const onShortcut = (event) => {
      if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey)
        return;
      if (showLeaveGuard || showPaletteModal) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[role="dialog"], .modal, [contenteditable="true"]'))
        return;
      const key = event.key.toLowerCase();
      const insidePage = wrapperRef.current?.contains(target);
      const insideGrid = listRef.current?.contains(target);
      const bodyHasFocus =
        target === document.body || target === document.documentElement;
      const outsideEditable = !target.closest(
        'input, textarea, select, [role="textbox"]'
      );

      if (key === "s") {
        if (!isLocked && !event.repeat) {
          event.preventDefault();
          if (hasDirty) saveAll();
        }
        return;
      }
      if (!insidePage && !bodyHasFocus) return;

      // Native selection/copy/paste applies to the field while editing.
      // The paste event still recognizes and inserts Created Item payloads.
      if (
        insideGrid &&
        ((target instanceof HTMLInputElement && ["text", "number"].includes(target.type)) || target instanceof HTMLTextAreaElement) &&
        !target.readOnly &&
        ["a", "c", "v"].includes(key)
      ) return;

      if (key === "z" || key === "y") {
        if (!insideGrid && !outsideEditable) return;
        event.preventDefault();
        if (isLocked || savingRef.current || event.repeat) return;
        navigateHistory(key === "z" ? "undo" : "redo");
        return;
      }

      if (
        key === "v" &&
        event.repeat &&
        (insideGrid || bodyHasFocus || target === addButtonRef.current)
      ) {
        event.preventDefault();
        return;
      }

      if (key === "a" && !event.repeat && !isLocked) {
        if (!insideGrid && !outsideEditable) return;
        event.preventDefault();
        window.getSelection()?.removeAllRanges();
        setCellRange(null);
        setSelectedIds(new Set(visibleItems.map((item) => item.itemId)));
      }
    };
    window.addEventListener("keydown", onShortcut, true);
    return () => window.removeEventListener("keydown", onShortcut, true);
  }, [
    visibleItems,
    isLocked,
    showLeaveGuard,
    showPaletteModal,
    hasDirty,
    saveAll,
    navigateHistory
  ]);

  const addNewItem = useCallback(() => {
    if (isLocked) return;
    if (focusEmptyDraft()) return;
    const id = -Date.now();
    const lastItem = visibleItems[visibleItems.length - 1];
    const newItem = {
      itemId: id,
      name: "",
      work: { workId: workIdNum },
      object: null,
      sizeArr: [0, 0, 0],
      size: buildSizeString(0, 0, 0),
      kant: buildKantString("-", 0, 0),
      cantType: "-",
      longCant: 0,
      shortCant: 0,
      qty: 1,
      details: "",
      rotable: true,
      rotation: 0,
      position: "",
      milling: lastItem?.milling ?? "",
      colorId:
        lastItem?.colorId ??
        lastItem?.color?.colorId ??
        (noColorOnly ? null : (selectedColorId ?? null))
    };
    pendingFocusRef.current = { rowId: id, colIdx: COL.H, selectText: true };
    setItems((prev) => [...prev, newItem]);
    setSelectedItemId(id);
    requestAnimationFrame(() => {
      const sc = listRef.current;
      if (sc) sc.scrollTop = sc.scrollHeight;
    });
  }, [
    isLocked,
    workIdNum,
    noColorOnly,
    selectedColorId,
    visibleItems,
    focusEmptyDraft
  ]);

  return (
    <>
    {isSaving && (
      <div className="created-items-saving-overlay" role="status" aria-live="polite">
        <span className="created-items-save-spinner" aria-hidden="true" />
        {t("createdItemsSaving")}
      </div>
    )}
    <div
      className="created-items-page container d-xl-block"
      ref={wrapperRef}
      inert={isSaving ? true : undefined}
      aria-busy={isSaving}
      onMouseDown={handleBlankClick}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        padding: 12,
        paddingTop: "1rem",
        paddingBottom: "1rem",
        height: "95dvh",
        minHeight: 0,
        background: "#f7f8fb",
        userSelect: "none",
        WebkitUserSelect: "none"
      }}
    >
      <style>{`
        .created-items-page input:not([type="checkbox"]),
        .created-items-page textarea,
        .created-items-page [contenteditable="true"] {
          user-select: text;
          -webkit-user-select: text;
        }
        .created-items-page .created-items-grid-scroller,
        .created-items-page .created-items-toolbar,
        .created-items-page .created-items-add-button {
          -webkit-touch-callout: none;
        }
      `}</style>
      {showLeaveGuard && (
        <div style={overlayStyle}>
          <div
            ref={leaveDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="unsaved-changes-title"
            className="created-items-leave-dialog"
            style={modalStyle}
          >
            <h3 id="unsaved-changes-title">{t("unsavedChangesTitle")}</h3>
            <p>{t("unsavedChangesBody")}</p>
            <div
              style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}
            >
              <button
                onClick={async () => {
                  await saveAll();
                  setShowLeaveGuard(false);
                  if (pendingPath === -1) navigate(-1);
                  else navigate(pendingPath);
                }}
                style={btnStyle("primary")}
              >
                {t("saveAndContinue")}
              </button>
              <button
                onClick={() => {
                  setItems(
                    originalRef.current.map((item) => ({
                      ...item,
                      sizeArr: [...item.sizeArr]
                    }))
                  );
                  setPendingDeletes(new Set());
                  clearAllSelection();
                  pendingFocusRef.current = null;
                  navLockRef.current = false;
                  setHasDirty(false);
                  setShowLeaveGuard(false);
                  if (pendingPath === -1) navigate(-1);
                  else navigate(pendingPath);
                }}
                style={btnStyle("danger")}
              >
                {t("discardChanges")}
              </button>
              <button
                ref={cancelLeaveRef}
                onClick={() => setShowLeaveGuard(false)}
                style={btnStyle("ghost")}
              >
                {t("cancelBtn")}
              </button>
            </div>
          </div>
        </div>
      )}

      <ColorPickerModal
        show={!isLocked && showPaletteModal}
        colors={allColors}
        onClose={closePalette}
        onSelect={(color) => applyBulkPatch({ colorId: color.colorId })}
        millings={allMillings}
        onSelectMilling={(milling, variantId, selectedColor) =>
          applyBulkPatch({
            ...(selectedColor ? { colorId: selectedColor.colorId } : {}),
            millingId: milling?.millingId ?? null,
            millingVariantId: variantId || ""
          })
        }
      />

      <div className="created-items-toolbar" style={toolbarStyle}>
        {/* Bal oldal – összenyomható, bulk kontrolok */}
        <div
          className="created-items-toolbar-left"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flex: "1 1 0",
            minWidth: 0,
            overflow: "hidden"
          }}
        >
          <select
            value={
              noColorOnly
                ? "__none__"
                : selectedColorId != null
                  ? String(selectedColorId)
                  : "__all__"
            }
            onChange={(e) => {
              const v = e.target.value;
              if (v === "__all__") {
                setNoColorOnly(false);
                setSelectedColorId(null);
              } else if (v === "__none__") {
                setNoColorOnly(true);
                setSelectedColorId(null);
              } else {
                setNoColorOnly(false);
                setSelectedColorId(Number(v));
              }
            }}
            style={{ ...pillSelectStyle, flexShrink: 0 }}
          >
            <option value="__all__">{t("allColors")}</option>
            <option value="__none__">{t("noColors")}</option>
            {palette.map((c) => (
              <option key={c.colorId} value={String(c.colorId)}>
                {t(c.name) || c.name || `#${c.colorId}`}
              </option>
            ))}
          </select>

          {selectedIds.size > 0 && !isLocked && (
            <>
              <span
                style={{
                  fontSize: 12,
                  color: "#666",
                  whiteSpace: "nowrap",
                  flexShrink: 0
                }}
              >
                {selectedIds.size} kijelölve
              </span>
              <select
                onChange={(e) => {
                  if (e.target.value === "") return;
                  bulkSetRotable(e.target.value === "1");
                  e.target.value = "";
                }}
                defaultValue=""
                style={{ ...pillSelectStyle, width: "7rem", flexShrink: 0 }}
                title={t("bulkRotable")}
              >
                <option value="">{t("rotatable")}</option>
                <option value="1">{t("rotable")}</option>
                <option value="0">{t("notRotable")}</option>
              </select>
              <select
                onChange={(e) => {
                  if (!e.target.value) return;
                  bulkSetCantType(e.target.value);
                  e.target.value = "";
                }}
                defaultValue=""
                style={{ ...pillSelectStyle, width: "6rem", flexShrink: 0 }}
                title={t("bulkCantType")}
              >
                <option value="">{t("edgeType")}</option>
                {["-", "04", "2", "42", "1"].map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
              <select
                onChange={(e) => {
                  if (e.target.value === "") return;
                  bulkSetLongShort("long", e.target.value);
                  e.target.value = "";
                }}
                defaultValue=""
                style={{ ...pillSelectStyle, width: "7rem", flexShrink: 0 }}
                title={t("longEdgeCount")}
              >
                <option value="">{t("longEdge")}</option>
                <option value="0">0</option>
                <option value="1">1</option>
                <option value="2">2</option>
              </select>
              <select
                onChange={(e) => {
                  if (e.target.value === "") return;
                  bulkSetLongShort("short", e.target.value);
                  e.target.value = "";
                }}
                defaultValue=""
                style={{ ...pillSelectStyle, width: "6.5rem", flexShrink: 0 }}
                title={t("shortEdgeCount")}
              >
                <option value="">{t("shortEdge")}</option>
                <option value="0">0</option>
                <option value="1">1</option>
                <option value="2">2</option>
              </select>
              <input
                type="number"
                min={0}
                max={FIELD_LIMITS.qty}
                placeholder={t("quantityShort")}
                onChange={(e) => bulkSetQty(e.target.value)}
                style={{ ...pillSelectStyle, width: 80, flexShrink: 0 }}
                title={t("quantityShort")}
              />
            </>
          )}
        </div>

        {/* Jobb oldal – fixált, sosem törik */}
        <div
          className="created-items-toolbar-right"
          style={{
            display: "flex",
            gap: 6,
            alignItems: "center",
            flexShrink: 0
          }}
        >
          {!isLocked && (
            <>
              <button
                onClick={() => navigateHistory("undo")}
                disabled={isSaving || !historyAvailability.undo}
                aria-label={t("createdItemsUndo")}
                title={`${t("createdItemsUndo")} (Ctrl+Z)`}
                style={btnStyle(historyAvailability.undo ? "ghost" : "muted")}
              >
                <IonIcon icon={arrowUndoOutline} aria-hidden="true" style={{ fontSize: 18, verticalAlign: "middle" }} />
              </button>
              <button
                onClick={() => navigateHistory("redo")}
                disabled={isSaving || !historyAvailability.redo}
                aria-label={t("createdItemsRedo")}
                title={`${t("createdItemsRedo")} (Ctrl+Y)`}
                style={btnStyle(historyAvailability.redo ? "ghost" : "muted")}
              >
                <IonIcon icon={arrowRedoOutline} aria-hidden="true" style={{ fontSize: 18, verticalAlign: "middle" }} />
              </button>
              <button
                onClick={saveAll}
                disabled={isSaving || !hasDirty}
                aria-busy={isSaving}
                style={btnStyle(!hasDirty ? "muted" : "primary")}
              >
                {isSaving && <span className="created-items-save-spinner" aria-hidden="true" />}
                {t("save")}
              </button>
              {rowsToDelete.length > 0 && (
                <button style={btnStyle("danger")} onClick={deleteSelected}>
                  {t("deleteSelected")}
                </button>
              )}
              <button onClick={openPaletteForCurrent} style={btnStyle("ghost")}>
                {selectedColorId != null && !noColorOnly
                  ? t("replaceColor")
                  : t("addColor")}
              </button>
            </>
          )}
        </div>
      </div>

      <div
        className="created-items-grid-scroller"
        data-list-scroller
        ref={listRef}
        onPointerDownCapture={beginRowDrag}
        onClickCapture={(event) => {
          if (!suppressDragClickRef.current) return;
          event.preventDefault();
          event.stopPropagation();
          suppressDragClickRef.current = false;
        }}
        onDragStart={(event) => event.preventDefault()}
        style={{
          overflowY: "scroll",
          overflowX: "auto",
          height: "86.5dvh",
          borderRadius: 8,
          background: "#fff",
          userSelect: "none",
          WebkitUserSelect: "none",
          flex: "1 1 auto",
          minHeight: 0
        }}
      >
        <HeaderRow
          innerRef={headerRef}
          allSelected={allVisibleSelected}
          onToggleAll={toggleSelectAllVisible}
        />
        {!isLocked && (
          <button
            type="button"
            ref={addButtonRef}
            className="created-items-add-button"
            onClick={addNewItem}
            onKeyDown={(event) => {
              if (event.key !== "ArrowDown" || visibleItems.length === 0)
                return;
              event.preventDefault();
              focusCell(0, COL.H);
              gridRefs.current[0]?.[COL.H]?.current?.select?.();
            }}
            style={{
              position: "sticky",
              top: THEME.headerHeight - 2,
              zIndex: 1,
              width: "100%",
              padding: "8px 12px",
              textAlign: "center",
              fontSize: THEME.fontSize,
              color: "#0d6efd",
              cursor: "pointer",
              borderLeft: "none",
              borderRight: "none",
              borderBottom: `1px solid ${THEME.headerBorder}`,
              borderTop: `1px solid ${THEME.headerBorder}`,
              background: "#fff",
              userSelect: "none",
              letterSpacing: 0.2
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f5ff")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
          >
            {t("addNewItem")}
          </button>
        )}
        {visibleItems.map((row, rIdx) => {
          const [h, w, rowT] = row.sizeArr ?? parseSize(row.size);
          const selected = isSelectedMulti(row.itemId);

          if (!gridRefs.current[rIdx]) gridRefs.current[rIdx] = [];
          for (let c = 0; c < COLS; c++) {
            gridRefs.current[rIdx][c] =
              gridRefs.current[rIdx][c] || React.createRef();
          }

          // colorId: lokális colorId elsőbbséget kap a szerver color.colorId felett
          const colorId = row.colorId ?? row.color?.colorId ?? null;
          const colorObj =
            colorId != null
              ? (allColors.find((c) => c.colorId === colorId) ?? null)
              : null;

          const materialType = colorObj?.materialType ?? null;

          const onChange = (patch, editedCol) => {
            if (isLocked) return;
            setItems((prev) =>
              prev.map((x) => {
                const rangeApplies =
                  cellRange?.ids.includes(row.itemId) &&
                  cellRange.colIdx === (editedCol ?? activeCell.colIdx);
                const included =
                  x.itemId === row.itemId ||
                  (selectedIds.has(row.itemId) && selectedIds.has(x.itemId)) ||
                  (rangeApplies && cellRange.ids.includes(x.itemId));
                if (!included || pendingDeletes.has(x.itemId)) return x;
                const safePatch = { ...patch };
                if ("name" in safePatch)
                  safePatch.name = String(safePatch.name ?? "").slice(
                    0,
                    FIELD_LIMITS.name
                  );
                if ("details" in safePatch)
                  safePatch.details = String(safePatch.details ?? "").slice(
                    0,
                    FIELD_LIMITS.details
                  );
                if ("qty" in safePatch)
                  safePatch.qty = clampNumber(safePatch.qty, FIELD_LIMITS.qty);
                if (safePatch.sizeArr) {
                  safePatch.sizeArr = [...safePatch.sizeArr];
                  safePatch.sizeArr[0] = clampNumber(
                    safePatch.sizeArr[0],
                    FIELD_LIMITS.length
                  );
                  safePatch.sizeArr[1] = clampNumber(
                    safePatch.sizeArr[1],
                    FIELD_LIMITS.width
                  );
                }
                let next = { ...x, ...safePatch };
                if (
                  "millingId" in safePatch &&
                  safePatch.millingId !== x.millingId &&
                  !("millingVariantId" in safePatch)
                )
                  next.millingVariantId = "";
                if (safePatch.sizeArr) {
                  const dimension = editedCol === COL.H ? 0 : 1;
                  next.sizeArr = [...x.sizeArr];
                  next.sizeArr[dimension] = safePatch.sizeArr[dimension];
                  const [nh, nw, nt] = next.sizeArr;
                  next.size = buildSizeString(nh, nw, nt);
                }
                if (
                  "cantType" in safePatch ||
                  "longCant" in safePatch ||
                  "shortCant" in safePatch
                ) {
                  const ct = safePatch.cantType ?? x.cantType;
                  const lc = safePatch.longCant ?? x.longCant;
                  const sc = safePatch.shortCant ?? x.shortCant;
                  next.kant = buildKantString(ct, lc, sc);
                }
                return next;
              })
            );
          };

          const handleArrowNav = (colIdx, e) => {
            let k = e.key;
            const isRowCompletionCell = colIdx === COL.SHORT_CANT ||
              (colIdx === COL.QTY && (materialType === "MDF" || materialType === "PFL"));
            if (k === "Enter" && isRowCompletionCell &&
                !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
              e.preventDefault();
              e.stopPropagation();
              if (isLocked || savingRef.current || e.repeat || e.nativeEvent?.isComposing) return;
              setEditingCell(null);
              addNewItem();
              return;
            }
            if (k === "Enter" && colIdx === COL.LONG_CANT &&
                !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
              e.preventDefault();
              e.stopPropagation();
              if (isLocked || savingRef.current || e.repeat || e.nativeEvent?.isComposing) return;
              setEditingCell(null);
              k = "ArrowRight";
            }
            const editableColumns = [
              COL.NAME,
              COL.H,
              COL.W,
              COL.QTY,
              COL.DETAILS
            ];
            const isEditable = editableColumns.includes(colIdx) && !isLocked;
            const isEditing =
              editingCell?.rowId === row.itemId &&
              editingCell?.colIdx === colIdx;

            // Enter starts editing, or finishes editing and advances one cell.
            if (k === "Enter" && isEditable && !e.ctrlKey && !e.metaKey) {
              e.preventDefault();
              e.stopPropagation();
              if (e.repeat || e.nativeEvent?.isComposing) return;
              if (isEditing) {
                setEditingCell(null);
                k = "ArrowRight";
              } else {
                setEditingCell({ rowId: row.itemId, colIdx });
                requestAnimationFrame(() => {
                  gridRefs.current?.[rIdx]?.[colIdx]?.current?.select?.();
                });
                return;
              }
            }
            if (k === "Escape" && isEditing) {
              e.preventDefault();
              setEditingCell(null);
              return;
            }
            // Vertical arrows leave editing and continue with row navigation.
            // Horizontal arrows still move the cursor within the input.
            if (isEditing && e.key !== "Enter" && k !== "ArrowUp" && k !== "ArrowDown") return;
            // Type a character to begin editing and replace the cell's content.
            if (
              isEditable &&
              !e.ctrlKey &&
              !e.altKey &&
              !e.metaKey &&
              k.length === 1 &&
              !e.isComposing
            ) {
              const isNumeric = [COL.H, COL.W, COL.QTY].includes(colIdx);
              if (isNumeric && !/^[0-9]$/.test(k)) {
                e.preventDefault();
                return;
              }
              e.preventDefault();
              setEditingCell({ rowId: row.itemId, colIdx });
              if (colIdx === COL.NAME) onChange({ name: k });
              if (colIdx === COL.DETAILS) onChange({ details: k });
              if (colIdx === COL.H)
                onChange(
                  { sizeArr: [Number(k), row.sizeArr[1], row.sizeArr[2]] },
                  COL.H
                );
              if (colIdx === COL.W)
                onChange(
                  { sizeArr: [row.sizeArr[0], Number(k), row.sizeArr[2]] },
                  COL.W
                );
              if (colIdx === COL.QTY) onChange({ qty: Number(k) });
              requestAnimationFrame(() => {
                const input = gridRefs.current?.[rIdx]?.[colIdx]?.current;
                if (input?.setSelectionRange && input.type !== "number") {
                  input.setSelectionRange(
                    input.value.length,
                    input.value.length
                  );
                }
              });
              return;
            }

            if (
              k !== "ArrowLeft" &&
              k !== "ArrowRight" &&
              k !== "ArrowUp" &&
              k !== "ArrowDown"
            )
              return;
            if (e.ctrlKey || e.metaKey || e.altKey) return;
            e.preventDefault();
            setEditingCell(null);
            if (navLockRef.current) return;
            if (e.shiftKey && (k === "ArrowUp" || k === "ArrowDown")) {
              const nextR = Math.max(
                0,
                Math.min(
                  visibleItems.length - 1,
                  rIdx + (k === "ArrowDown" ? 1 : -1)
                )
              );
              if (!gridRefs.current[nextR]?.[colIdx]?.current) return;
              const anchorId =
                cellRange?.colIdx === colIdx ? cellRange.anchorId : row.itemId;
              const anchorIndex = visibleItems.findIndex(
                (item) => item.itemId === anchorId
              );
              const start = anchorIndex < 0 ? rIdx : anchorIndex;
              setCellRange({
                anchorId,
                colIdx,
                ids: visibleItems
                  .slice(Math.min(start, nextR), Math.max(start, nextR) + 1)
                  .map((item) => item.itemId)
              });
              navLockRef.current = true;
              requestAnimationFrame(() => {
                focusCell(nextR, colIdx);
                gridRefs.current[nextR]?.[colIdx]?.current?.select?.();
                navLockRef.current = false;
              });
              return;
            }
            setCellRange(null);

            // ArrowDown az utolsó soron → új sor a végére
            if (k === "ArrowDown" && rIdx === visibleItems.length - 1) {
              if (isLocked) return;
              if (focusEmptyDraft()) return;
              const id = -Date.now();
              const ref = visibleItems[visibleItems.length - 1];
              const newItem = {
                itemId: id,
                name: "",
                work: { workId: workIdNum },
                object: null,
                sizeArr: [...ref.sizeArr],
                size: buildSizeString(...ref.sizeArr),
                kant: buildKantString(
                  ref?.cantType ?? "-",
                  ref?.longCant ?? 0,
                  ref?.shortCant ?? 0
                ),
                cantType: ref?.cantType ?? "-",
                longCant: ref?.longCant ?? 0,
                shortCant: ref?.shortCant ?? 0,
                qty: 1,
                rotable: ref?.rotable ?? true,
                millingId: ref?.millingId ?? ref?.milling?.millingId ?? null,
                milling: ref?.milling ?? null,
                millingVariantId: ref?.millingVariantId ?? "",
                colorId: ref?.colorId ?? ref?.color?.colorId ?? null
              };
              navLockRef.current = true;
              pendingFocusRef.current = {
                rowId: id,
                colIdx: COL.H,
                selectText: true
              };

              setItems((prev) => [...prev, newItem]);

              requestAnimationFrame(() => {
                const sc = listRef.current;
                if (sc) sc.scrollTop = sc.scrollHeight;
              });
              return;
            }

            // ArrowUp az első soron → új sor a legelejére
            if (k === "ArrowUp" && rIdx === 0) {
              if (isLocked) return;
              if (focusEmptyDraft()) return;
              const id = -Date.now();
              const ref = visibleItems[0];
              const newItem = {
                itemId: id,
                name: "",
                work: { workId: workIdNum },
                object: null,
                sizeArr: [...ref.sizeArr],
                size: buildSizeString(...ref.sizeArr),
                kant: buildKantString(
                  ref?.cantType ?? "-",
                  ref?.longCant ?? 0,
                  ref?.shortCant ?? 0
                ),
                cantType: ref?.cantType ?? "-",
                longCant: ref?.longCant ?? 0,
                shortCant: ref?.shortCant ?? 0,
                qty: 1,
                details: "",
                rotable: ref?.rotable ?? true,

                rotation: 0,
                position: "",
                millingId: ref?.millingId ?? ref?.milling?.millingId ?? null,
                milling: ref?.milling ?? null,
                millingVariantId: ref?.millingVariantId ?? "",
                colorId: ref?.colorId ?? ref?.color?.colorId ?? null
              };
              navLockRef.current = true;
              pendingFocusRef.current = { rowId: id, colIdx };

              setItems((prev) => [newItem, ...prev]);

              requestAnimationFrame(() => {
                const sc = listRef.current;
                if (sc) sc.scrollTop = 0;
              });
              return;
            }

            let nextR = rIdx;
            let nextC = colIdx;
            if (k === "ArrowLeft") nextC = colIdx - 1;
            if (k === "ArrowRight") nextC = colIdx + 1;
            if (k === "ArrowUp") nextR = rIdx - 1;
            if (k === "ArrowDown") nextR = rIdx + 1;
            nextR = Math.max(0, Math.min(nextR, visibleItems.length - 1));
            nextC = Math.max(0, Math.min(nextC, COLS - 1));
            const direction = k === "ArrowLeft" ? -1 : 1;
            while (
              nextC >= 0 &&
              nextC < COLS &&
              !gridRefs.current[nextR]?.[nextC]?.current
            ) {
              nextC += direction;
            }
            if (nextC < 0 || nextC >= COLS) return;
            const targetRowId = visibleItems[nextR]?.itemId;
            if (targetRowId != null) {
              if (selectedItemId !== targetRowId)
                setSelectedItemId(targetRowId);
              if (
                activeCell?.rowId !== targetRowId ||
                activeCell?.colIdx !== nextC
              ) {
                setActiveCell({ rowId: targetRowId, colIdx: nextC });
              }
            }
            navLockRef.current = true;
            requestAnimationFrame(() => {
              focusCell(nextR, nextC);
              navLockRef.current = false;
            });
          };

          const onFocusCell = (colIdx) => {
            if (
              editingCell &&
              (editingCell.rowId !== row.itemId ||
                editingCell.colIdx !== colIdx)
            ) {
              setEditingCell(null);
            }
            setCellRange((range) =>
              range &&
              (range.colIdx !== colIdx || !range.ids.includes(row.itemId))
                ? null
                : range
            );
            if (selectedItemId !== row.itemId) setSelectedItemId(row.itemId);
            if (
              activeCell?.rowId !== row.itemId ||
              activeCell?.colIdx !== colIdx
            ) {
              setActiveCell({ rowId: row.itemId, colIdx });
            }
          };

          return (
            <RowEditable
              key={row.itemId}
              selected={selected}
              rangeCol={
                cellRange?.ids.includes(row.itemId) ? cellRange.colIdx : null
              }
              allMillings={allMillings}
              activeCell={activeCell}
              editingCell={editingCell}
              rowId={row.itemId}
              rowNumber={rIdx + 1}
              colorObj={colorObj}
              allColors={allColors}
              value={{
                name: row.name || "",
                details: row.details || "",
                milling: row.milling || "",
                colorId: row.colorId ?? null,
                millingId: row.millingId ?? null,
                millingVariantId: row.millingVariantId || "",
                materialType,
                sizeArr: [h, w, rowT],
                longCant: row.longCant ?? 0,
                shortCant: row.shortCant ?? 0,
                cantType: row.cantType ?? "-",
                qty: row.qty ?? 0,
                rotable: row.rotable ?? true
              }}
              onClick={(e) => onRowActivate(e, row.itemId)}
              onFocusRow={() =>
                setSelectedItemId((prev) =>
                  prev === row.itemId ? prev : row.itemId
                )
              }
              onFocusCell={onFocusCell}
              onChange={onChange}
              inputRefs={gridRefs.current[rIdx]}
              onKeyDownAt={(colIdx, e) => handleArrowNav(colIdx, e)}
              disabled={isLocked}
              checked={selected}
              onCheck={(e) => handleRowSelect(row.itemId, e)}
              onDeleteRow={() => markDeleted(row.itemId)}
            />
          );
        })}
        {visibleItems.length === 0 && (
          <div style={{ padding: 8, color: "#777" }}>
            {t("noItemsForFilter")}
          </div>
        )}
      </div>
    </div>
    </>
  );
}

/* ----------------- Header ----------------- */

const hCell = (extra = {}) => ({
  padding: THEME.padHeader,
  fontSize: THEME.fontSize,
  fontWeight: 600,
  lineHeight: 1.2,
  borderRight: `1px solid ${THEME.headerBorder}`,
  borderBottom: `1px solid ${THEME.headerBorder}`,
  background: THEME.headerBg,
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  ...extra
});

function HeaderRow({ innerRef, allSelected, onToggleAll }) {
  const { t } = useLanguage();
  return (
    <div
      ref={innerRef}
      style={{
        display: "grid",
        gridTemplateColumns: GRID_TEMPLATE,
        gap: 0,
        background: THEME.headerBg,
        position: "sticky",
        top: 0,
        zIndex: 2,
        borderLeft: `1px solid ${THEME.headerBorder}`
      }}
    >
      {/* Checkbox */}
      <div
        style={hCell({
          textAlign: "center",
          padding: THEME.padCell,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        })}
      >
        <input
          type="checkbox"
          checked={!!allSelected}
          onChange={onToggleAll}
          aria-label={t("selectAllVisible")}
          style={{
            margin: 0,
            transform: "scale(0.9)",
            display: "block",
            marginInline: "auto"
          }}
        />
      </div>
      <div style={hCell({ textAlign: "center" })}>{t("nr")}</div>
      <div style={hCell({ textAlign: "center" })}>{t("color")}</div>
      <div style={hCell({ textAlign: "center" })}>{t("millings")}</div>
      <div style={hCell({ textAlign: "center" })}>{t("material")}</div>
      <div style={hCell({ textAlign: "center" })}>{t("name")}</div>
      <div style={hCell({ textAlign: "center" })}>{t("length")}</div>
      <div style={hCell({ textAlign: "center" })}>{t("width")}</div>
      <div style={hCell({ textAlign: "center" })}>{t("quantityShort")}</div>
      {/* ↓ rövidített feliratok */}
      <div style={hCell({ textAlign: "center" })} title={t("longEdgeCount")}>
        {t("longEdgeCount")}
      </div>
      <div style={hCell({ textAlign: "center" })} title={t("shortEdgeCount")}>
        {t("shortEdgeCount")}
      </div>
      <div style={hCell({ textAlign: "center" })}>{t("edgeType")}</div>
      <div style={hCell({ textAlign: "center" })}>{t("rotatableShort")}</div>
      <div style={hCell({ textAlign: "center" })}>{t("details")}</div>
      <div style={hCell({ textAlign: "center", borderRight: "none" })}></div>
    </div>
  );
}

/* ----------------- RowEditable ----------------- */
const RowEditable = memo(function RowEditable({
  value,
  selected,
  rangeCol,
  rowId,
  rowNumber,
  colorObj,
  allColors,
  activeCell,
  editingCell,
  onChange,
  onClick,
  materialType,
  onFocusRow,
  onFocusCell,
  inputRefs,
  allMillings,
  onKeyDownAt,
  disabled,
  checked,
  onCheck,
  onDeleteRow
}) {
  const [h, w] = value.sizeArr;
  const [isHover, setIsHover] = useState(false);
  const [colorOpen, setColorOpen] = useState(false);
  const restoreColorFocus = () =>
    inputRefs[COL.COLOR]?.current?.focus({ preventScroll: true });

  const isEditCell = (colIdx) =>
    editingCell?.rowId === rowId && editingCell?.colIdx === colIdx;

  const isCellActive = (colIdx) =>
    activeCell?.rowId === rowId && activeCell?.colIdx === colIdx;

  const setCellRef = (colIdx, node) => {
    if (inputRefs && inputRefs[colIdx]) inputRefs[colIdx].current = node;
  };

  const rowBg = computeCellBg({
    activeCell,
    rowId,
    colIdx: -1,
    selected,
    isHover
  });
  const iconBtn = makeIconDeleteButtonStyle();

  const renderInputStyle = (colIdx, align) => {
    const bg = computeCellBg({ activeCell, rowId, colIdx, selected, isHover });
    const highlighted = isCellActive(colIdx) || rangeCol === colIdx;
    return makeInputCellStyle({
      bg: highlighted ? THEME.colors.cellActiveBg : bg,
      active: highlighted,
      align
    });
  };

  const { t } = useLanguage();

  return (
    <div
      data-row="1"
      data-row-id={rowId}
      onClick={onClick}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      style={{
        display: "grid",
        gridTemplateColumns: GRID_TEMPLATE,
        gap: 0,
        borderBottom: `1px solid ${THEME.cellBorder}`,
        background: rowBg,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.95 : 1
      }}
    >
      {/* Checkbox */}
      <div
        onClick={(e) => {
          if (!disabled && e.target.type !== "checkbox") {
            inputRefs[COL.SELECT]?.current?.focus();
            onCheck(e);
          }
        }}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRight: `1px solid ${THEME.cellBorder}`,
          background: renderInputStyle(COL.SELECT).background
        }}
      >
        <input
          type="checkbox"
          ref={(n) => setCellRef(COL.SELECT, n)}
          onFocus={() => onFocusCell?.(COL.SELECT)}
          onKeyDown={(e) => onKeyDownAt?.(COL.SELECT, e)}
          checked={!!checked}
          onChange={(e) => onCheck(e)}
          aria-label={t("selectRow")}
          disabled={disabled}
          style={{ margin: 0, transform: "scale(0.9)" }}
        />
      </div>

      {/* Nr. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: THEME.fontSize,
          color: "#888",
          borderRight: `1px solid ${THEME.cellBorder}`,
          height: THEME.rowHeight,
          background: rowBg,
          userSelect: "none"
        }}
      >
        {rowNumber}
      </div>

      {/* Szín – kattintható chip + egyedi dropdown */}
      <div
        ref={(n) => setCellRef(COL.COLOR, n)}
        tabIndex={disabled ? -1 : 0}
        role="button"
        aria-label={t("color")}
        onFocus={(e) => {
          if (e.target === e.currentTarget) onFocusCell?.(COL.COLOR);
        }}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (!disabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            e.stopPropagation();
            e.currentTarget.focus();
            setColorOpen(true);
          } else onKeyDownAt?.(COL.COLOR, e);
        }}
        style={{
          ...renderInputStyle(COL.COLOR),
          position: "relative",
          height: THEME.rowHeight,
          borderRight: `1px solid ${THEME.cellBorder}`,

          minWidth: 0
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) {
            e.currentTarget.focus();
            setColorOpen(true);
          }
        }}
      >
        {/* trigger */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: "0 4px",
            height: "100%",
            cursor: disabled ? "default" : "pointer",
            overflow: "hidden",
            minWidth: 0
          }}
        >
          {colorObj ? (
            <>
              {colorObj.imageDataReduced ? (
                <img
                  src={`data:image/jpeg;base64,${colorObj.imageDataReduced}`}
                  alt={t(colorObj.name) || colorObj.name}
                  style={{
                    width: 20,
                    height: 20,
                    objectFit: "cover",
                    borderRadius: 3,
                    flexShrink: 0
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 20,
                    height: 20,
                    background: colorObj.hex || "#ddd",
                    borderRadius: 3,
                    flexShrink: 0,
                    border: "1px solid #ccc"
                  }}
                />
              )}
              <span
                style={{
                  fontSize: 11,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: renderInputStyle(COL.COLOR).color,
                  flex: 1
                }}
                title={t(colorObj.name) || colorObj.name}
              >
                {t(colorObj.name) || colorObj.name}
              </span>
            </>
          ) : (
            <span style={{ fontSize: 11, color: "#bbb" }}>–</span>
          )}
          {!disabled && (
            <span
              style={{
                fontSize: 9,
                color: "#aaa",
                marginLeft: "auto",
                flexShrink: 0
              }}
            >
              ▾
            </span>
          )}
        </div>

        <ColorPickerModal
          show={colorOpen && !disabled}
          colors={allColors}
          colorId={value.colorId ?? null}
          onClose={() => {
            setColorOpen(false);
            requestAnimationFrame(restoreColorFocus);
          }}
          onExited={restoreColorFocus}
          onSelect={(color) => onChange({ colorId: color.colorId })}
          millings={allMillings}
          onSelectMilling={(milling, variantId, selectedColor) =>
            onChange({
              ...(selectedColor ? { colorId: selectedColor.colorId } : {}),
              millingId: milling?.millingId ?? null,
              millingVariantId: variantId || ""
            })
          }
        />
      </div>

      {/* Marás – szabad szöveges input, lokális */}
      <MillingSelector
        innerRef={(n) => setCellRef(COL.MILLING, n)}
        onFocus={() => onFocusCell?.(COL.MILLING)}
        onKeyDown={(e) => onKeyDownAt?.(COL.MILLING, e)}
        styleOverride={renderInputStyle(COL.MILLING)}
        millingId={value.millingId ?? null}
        millingVariantId={value.millingVariantId || ""}
        width={value.sizeArr[1]}
        height={value.sizeArr[0]}
        colorId={value.colorId ?? null}
        allColors={allColors}
        allMillings={allMillings}
        onChange={onChange}
        disabled={disabled}
        bg={rowBg}
        rowHeight={THEME.rowHeight}
        fontSize={THEME.fontSize}
      />
      {/* Anyag típus – csak olvasható */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "0 6px",
          fontSize: THEME.fontSize,
          borderRight: `1px solid ${THEME.cellBorder}`,
          height: THEME.rowHeight,
          background: rowBg,
          color: "#555",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          userSelect: "none"
        }}
      >
        {value.materialType ?? "–"}
      </div>
      {/* Név */}
      <input
        ref={(n) => setCellRef(COL.NAME, n)}
        disabled={disabled}
        onFocus={() => {
          onFocusRow?.();
          onFocusCell?.(COL.NAME);
        }}
        value={value.name ? t(value.name) : ""}
        maxLength={FIELD_LIMITS.name}
        readOnly={!isEditCell(COL.NAME)}
        onChange={(e) => onChange({ name: e.target.value })}
        onKeyDown={(e) => onKeyDownAt?.(COL.NAME, e)}
        style={renderInputStyle(COL.NAME, "left")}
      />

      {/* Hosszúság */}
      <NumberInput
        innerRef={(n) => setCellRef(COL.H, n)}
        disabled={disabled}
        onFocus={() => {
          onFocusRow?.();
          onFocusCell?.(COL.H);
        }}
        value={h}
        max={FIELD_LIMITS.length}
        readOnly={!isEditCell(COL.H)}
        onKeyDown={(e) => onKeyDownAt?.(COL.H, e)}
        onChange={(nv) =>
          onChange({ sizeArr: [nv, w, value.sizeArr[2]] }, COL.H)
        }
        placeholder={t("length")}
        styleOverride={renderInputStyle(COL.H, "right")}
      />

      {/* Szélesség */}
      <NumberInput
        innerRef={(n) => setCellRef(COL.W, n)}
        disabled={disabled}
        onFocus={() => {
          onFocusRow?.();
          onFocusCell?.(COL.W);
        }}
        value={w}
        max={FIELD_LIMITS.width}
        readOnly={!isEditCell(COL.W)}
        onKeyDown={(e) => onKeyDownAt?.(COL.W, e)}
        onChange={(nv) =>
          onChange({ sizeArr: [h, nv, value.sizeArr[2]] }, COL.W)
        }
        placeholder={t("width")}
        styleOverride={renderInputStyle(COL.W, "right")}
      />

      {/* Darabszám */}
      <NumberInput
        innerRef={(n) => setCellRef(COL.QTY, n)}
        disabled={disabled}
        onFocus={() => {
          onFocusRow?.();
          onFocusCell?.(COL.QTY);
        }}
        value={value.qty}
        max={FIELD_LIMITS.qty}
        readOnly={!isEditCell(COL.QTY)}
        onKeyDown={(e) => onKeyDownAt?.(COL.QTY, e)}
        onChange={(nv) => onChange({ qty: nv })}
        placeholder={t("quantityShort")}
        styleOverride={renderInputStyle(COL.QTY, "center")}
      />

      {/* Hossz kant db */}
      {value.materialType === "MDF" || value.materialType === "PFL" ? (
        <div
          style={{
            borderRight: `1px solid ${THEME.cellBorder}`,
            height: THEME.rowHeight,
            background: rowBg
          }}
        />
      ) : (
        <SelectSmall
          innerRef={(n) => setCellRef(COL.LONG_CANT, n)}
          disabled={disabled}
          onFocus={() => {
            onFocusRow?.();
            onFocusCell?.(COL.LONG_CANT);
          }}
          value={String(value.longCant)}
          onKeyDown={(e) => onKeyDownAt?.(COL.LONG_CANT, e)}
          onChange={(s) => onChange({ longCant: Number(s) })}
          options={["0", "1", "2"]}
          styleOverride={renderInputStyle(COL.LONG_CANT, "center")}
        />
      )}

      {/* Szél kant db */}
      {value.materialType === "MDF" || value.materialType === "PFL" ? (
        <div
          style={{
            borderRight: `1px solid ${THEME.cellBorder}`,
            height: THEME.rowHeight,
            background: rowBg
          }}
        />
      ) : (
        <SelectSmall
          innerRef={(n) => setCellRef(COL.SHORT_CANT, n)}
          disabled={disabled}
          onFocus={() => {
            onFocusRow?.();
            onFocusCell?.(COL.SHORT_CANT);
          }}
          value={String(value.shortCant)}
          onKeyDown={(e) => onKeyDownAt?.(COL.SHORT_CANT, e)}
          onChange={(s) => onChange({ shortCant: Number(s) })}
          options={["0", "1", "2"]}
          styleOverride={renderInputStyle(COL.SHORT_CANT, "center")}
        />
      )}

      {/* Kant típus */}
      {value.materialType === "MDF" || value.materialType === "PFL" ? (
        <div
          style={{
            borderRight: `1px solid ${THEME.cellBorder}`,
            height: THEME.rowHeight,
            background: rowBg
          }}
        />
      ) : (
        <SelectSmall
          innerRef={(n) => setCellRef(COL.CANT_TYPE, n)}
          disabled={disabled}
          onFocus={() => {
            onFocusRow?.();
            onFocusCell?.(COL.CANT_TYPE);
          }}
          value={String(value.cantType)}
          onKeyDown={(e) => onKeyDownAt?.(COL.CANT_TYPE, e)}
          onChange={(s) => onChange({ cantType: normalizeCantTypeInput(s) })}
          options={cantTypeOptions.map(String)}
          styleOverride={renderInputStyle(COL.CANT_TYPE, "center")}
        />
      )}

      {/* Forgathatóság */}
      {value.materialType === "PFL" ? (
        <div style={{ borderRight: `1px solid ${THEME.cellBorder}`, height: THEME.rowHeight, background: rowBg }} />
      ) : (
      <RotableIconCell
        innerRef={(n) => setCellRef(COL.ROTABLE, n)}
        active={isCellActive(COL.ROTABLE) || rangeCol === COL.ROTABLE}
        bg={renderInputStyle(COL.ROTABLE).background}
        checked={!!value.rotable}
        onFocus={() => {
          onFocusRow?.();
          onFocusCell?.(COL.ROTABLE);
        }}
        onKeyDown={(e) => onKeyDownAt?.(COL.ROTABLE, e)}
        onToggle={() => onChange({ rotable: !value.rotable })}
        disabled={disabled}
        height={THEME.rowHeight}
      />
      )}

      {/* Megjegyzés */}
      <input
        ref={(n) => setCellRef(COL.DETAILS, n)}
        disabled={disabled}
        onFocus={() => {
          onFocusRow?.();
          onFocusCell?.(COL.DETAILS);
        }}
        value={value.details}
        maxLength={FIELD_LIMITS.details}
        readOnly={!isEditCell(COL.DETAILS)}
        onChange={(e) => onChange({ details: e.target.value })}
        onKeyDown={(e) => onKeyDownAt?.(COL.DETAILS, e)}
        placeholder=""
        style={renderInputStyle(COL.DETAILS, "left")}
      />

      {/* Törlés */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderLeft: `1px solid ${THEME.cellBorder}`,
          background: rowBg
        }}
      >
        <button
          ref={(n) => setCellRef(COL.DELETE, n)}
          onFocus={() => onFocusCell?.(COL.DELETE)}
          onKeyDown={(e) => onKeyDownAt?.(COL.DELETE, e)}
          onClick={onDeleteRow}
          style={{
            ...iconBtn,
            ...(isCellActive(COL.DELETE)
              ? {
                  background: THEME.colors.cellActiveBg,
                  color: "#fff",
                  outline: `2px solid ${THEME.focusOutline}`
                }
              : {})
          }}
          disabled={disabled}
          title={t("deleteRow")}
          aria-label={t("deleteRow")}
        >
          <IonIcon icon={trashOutline} style={{ fontSize: 16 }} />
        </button>
      </div>
    </div>
  );
});

/* ----------------- Mikró UI elemek ----------------- */

function NumberInput({
  value,
  onChange,
  placeholder,
  innerRef,
  onKeyDown,
  onFocus,
  styleOverride,
  disabled,
  max,
  readOnly
}) {
  return (
    <input
      ref={innerRef}
      disabled={disabled}
      type="number"
      readOnly={readOnly}
      min={0}
      max={max}
      value={Number.isFinite(value) ? value : ""}
      onChange={(e) =>
        onChange(
          max == null
            ? Number(e.target.value)
            : clampNumber(e.target.value, max)
        )
      }
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      placeholder={placeholder}
      style={styleOverride || inpStyle}
    />
  );
}

function SelectSmall({
  value,
  onChange,
  options,
  innerRef,
  onKeyDown,
  onFocus,
  styleOverride,
  disabled
}) {
  return (
    <select
      ref={innerRef}
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      style={{ ...(styleOverride || inpStyle) }}
    >
      {options.map((o) => (
        <option
          key={o}
          value={o}
          style={{ background: "#fff", color: "#212529" }}
        >
          {o}
        </option>
      ))}
    </select>
  );
}

function RotableIconCell({
  checked,
  onToggle,
  innerRef,
  onKeyDown,
  onFocus,
  active,
  disabled,
  bg = "white",
  height
}) {
  const h = Math.max(20, Number(height) || 0) || undefined;
  const iconSize = h ? Math.max(14, Math.min(20, h - 8)) : 20;
  const { t } = useLanguage();
  return (
    <div
      ref={innerRef}
      tabIndex={disabled ? -1 : 0}
      onFocus={onFocus}
      onKeyDown={(e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onToggle?.();
        }
        onKeyDown?.(e);
      }}
      onClick={(e) => {
        e.preventDefault();
        if (!disabled) {
          e.currentTarget.focus();
          onToggle?.();
        }
      }}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderLeft: `1px solid #eee`,
        borderRight: `1px solid #eee`,
        background: bg,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? "none" : "auto",
        userSelect: "none",
        padding: h ? "0 6px" : "0 8px",
        height: h,
        outline: "none"
      }}
      title={checked ? t("rotatable") : t("notRotatable")}
      aria-label={checked ? t("rotatable") : t("notRotatable")}
    >
      <span
        style={{
          position: "relative",
          width: iconSize + 2,
          height: iconSize + 2
        }}
      >
        <IonIcon
          icon={reload}
          style={{
            fontSize: iconSize,
            lineHeight: 1,
            verticalAlign: "middle",
            transform: checked ? "rotate(-20deg)" : "rotate(45deg)",
            opacity: checked ? 1 : 0.35,
            color: active ? "#fff" : undefined,
            transition: "transform 0.25s ease, opacity 0.2s ease"
          }}
        />
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "40%",
            top: "10%",
            width: 1.5,
            height: "110%",
            borderRadius: 50,
            background: active ? "#fff" : "#333",
            transform: checked
              ? "rotate(45deg) scaleY(0)"
              : "rotate(45deg) scaleY(1)",
            transformOrigin: "center",
            opacity: checked ? 0 : 1,
            transition: "transform 0.2s ease, opacity 0.2s ease",
            border: "none"
          }}
        />
      </span>
    </div>
  );
}
